import { Close } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useFetchData } from '../../../hooks/useFetchData';
import { bulkEditDataRequestItems } from '../../../services/dealGPT';
import { StyledIconButton } from '../../../theme/CustomComponents';
import { IntellioDataRequestItemSelection } from '../../../Types/dealGPT';
import { TreeNode } from '../../../Types/tree';
import { dataRequestListAllPathUrl } from '../../../util/routesUrls';
import { CheckboxTree } from '../../CheckboxTree';
import { useDrawer } from '../../Providers/DrawerProvider';

export interface DataRequestListDrawerProps {
  projectId: string;
  onSave?: () => void;
}

const DataRequestListDrawer: React.FC<DataRequestListDrawerProps> = ({ projectId, onSave }) => {
  const { data: items, loading } = useFetchData<IntellioDataRequestItemSelection[]>({
    urlPath: dataRequestListAllPathUrl(projectId),
  });

  const [selectedItems, setSelectedItems] = useState<IntellioDataRequestItemSelection[]>([]);

  const { closeDrawer } = useDrawer();

  const treeNodes: TreeNode<IntellioDataRequestItemSelection>[] = useMemo(() => {
    if (!items) return [];

    const map = new Map<string, TreeNode<IntellioDataRequestItemSelection>>();

    for (const item of items) {
      const scope = item.scope;

      if (!map.has(scope)) {
        map.set(scope, {
          id: scope,
          label: scope,
          children: [],
        });
      }

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      map.get(scope)!.children!.push({
        id: `${scope}:${item.key}`,
        label: `${item.key}: ${item.details}`,
        data: item,
      });
    }

    return Array.from(map.values());
  }, [items]);

  useEffect(() => {
    setSelectedItems(items?.filter((item) => item.selected) ?? []);
  }, [items]);

  const handleSave = useCallback(async () => {
    await bulkEditDataRequestItems(projectId, selectedItems);
    onSave?.();
    closeDrawer();
  }, [projectId, selectedItems, closeDrawer, onSave]);

  const hasChanges = useMemo(() => {
    if (!items) return false;
    const initialSelected = items.filter((item) => item.selected);
    const toId = (i: IntellioDataRequestItemSelection) => `${i.scope}:${i.key}`;

    const currentSet = new Set(selectedItems.map(toId));
    const initialSet = new Set(initialSelected.map(toId));

    if (currentSet.size !== initialSet.size) return true;

    for (const id of currentSet) {
      if (!initialSet.has(id)) return true;
    }

    return false;
  }, [items, selectedItems]);

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          height: '95px',
          display: 'flex',
          alignItems: 'center',
          padding: 2,
          justifyContent: 'space-between',
          borderBottom: '1px solid #ddd',
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Add or Remove Data Request Items
        </Typography>

        <StyledIconButton onClick={closeDrawer}>
          <Close />
        </StyledIconButton>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          padding: 2,
        }}
      >
        <Typography>Select the data request items you want to include in your project.</Typography>
        <CheckboxTree
          loading={loading}
          nodes={treeNodes}
          value={selectedItems}
          onChange={setSelectedItems}
          getNodeValue={(node) => node.data}
        />
      </Box>

      {/* Footer */}
      <Box
        sx={{
          padding: 2,
          borderTop: '1px solid #ddd',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 1,
          backgroundColor: 'background.paper',
        }}
      >
        <Button variant="outlined" onClick={closeDrawer}>
          Cancel
        </Button>

        <Button variant="contained" onClick={handleSave} disabled={!hasChanges}>
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default DataRequestListDrawer;
